import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { AppLocale, Config, LocaleBundle, UiLabels } from "../i18n/types";
import { getBundle } from "../i18n/bundles";
import { pickLocaleFromNavigator } from "../i18n/resolveLocale";

type LocaleContextValue = {
  locale: AppLocale;
  bundle: LocaleBundle;
  config: Config;
  ui: UiLabels;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocale] = useState<AppLocale>(() => pickLocaleFromNavigator());

  useEffect(() => {
    let cancelled = false;

    fetch("/api/locale", { credentials: "same-origin" })
      .then((res) => {
        if (!res.ok) throw new Error("locale api unavailable");
        return res.json() as Promise<{ locale?: string }>;
      })
      .then((data) => {
        const raw = data?.locale;
        const next: AppLocale = raw === "en" || raw === "it" ? raw : pickLocaleFromNavigator();
        if (!cancelled) setLocale(next);
      })
      .catch(() => {
        if (!cancelled) setLocale(pickLocaleFromNavigator());
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<LocaleContextValue>(() => {
    const bundle = getBundle(locale);
    return {
      locale,
      bundle,
      config: bundle.config,
      ui: bundle.ui,
    };
  }, [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return ctx;
}

/** Config contenuti localizzati (stesso uso di `config` importato da config.ts in precedenza). */
export function useSiteConfig(): Config {
  return useLocale().config;
}

export function useUiLabels(): UiLabels {
  return useLocale().ui;
}

export { pickLocaleFromAcceptLanguage, pickLocaleFromNavigator } from "../i18n/resolveLocale";
